import { actionSetErrorMessage, actionSetInfoMessage, AppMessagesContext, SessionContext, SettingsManager } from '@viamap/viamap2-common';
import { ReactElement, useCallback, useContext, useReducer, useRef } from 'react';
import { FRPActions, FRPContext, FRPReducer, initialFRPState, GenericTransactionManager, FRPState, FRPActionType, transactionalFRPReducer, encodeElementStateDataToPersistence, BroadcastedChanges, FRPElementType, FRPElement, revertAddProject } from './FRPState';
import { FRPProjectPersistence } from './FRPProjectPersistence';
import { FRPElementPersistence } from './FRPElementPersistence';
import { FRPNotification } from './FRPNotification';
import { MitWebsocket } from 'src/common/managers/MitWebsocket';
import { FRPFunc } from './FRPFunc';

export function FRPManager(props: { children: ReactElement }) {
   let { state: sessionState } = useContext(SessionContext);
   let { dispatch: appMessageDispatch } = useContext(AppMessagesContext);

   // Save State
   let PersistenceReducer = useCallback((state: FRPState, action: FRPActions) => {
      let nextState = FRPReducer(state, action);
      asyncPersistenceReducer(state, nextState, action);
      broadcastChanges(state, action);
      return nextState
   }, [sessionState.customerRef, sessionState.userRef])

   const [frpState, frpDispatch] = useReducer(PersistenceReducer, initialFRPState());
   const frpDispatchRef = useRef(frpDispatch);
   frpDispatchRef.current = frpDispatch;

   // This is responsible for persisting changes to the data store
   // It is called after the state has been updated by the reducer
   function asyncPersistenceReducer(oldState: FRPState, nextState: FRPState, action: FRPActions) {
      switch (action.type) {
         case FRPActionType.AddProject: {
            if ((oldState.availableProjectsList || []).some((p) => p.id === action.payload.id)) {
               frpDispatchRef.current(revertAddProject(action.payload.id));
               appMessageDispatch(actionSetErrorMessage(`Project '${action.payload.id}' already exists`));
               break;
            }

            const projectsElasticIndex = SettingsManager.getSystemSetting("frp.projectsElasticIndex", "");
            const userId = sessionState.userName;
            const projectsPersistence = new FRPProjectPersistence(projectsElasticIndex, userId);
            FRPProjectPersistence.setInstance(projectsPersistence);

            projectsPersistence.createProject(action.payload.item)
               .then(() => {
                  FRPElementPersistence.setInstance(new FRPElementPersistence(action.payload.item.esIndexName, userId));
                  appMessageDispatch(actionSetInfoMessage("Project added successfully"));
               })
               .catch((reason) => {
                  frpDispatchRef.current(revertAddProject(action.payload.id));
                  appMessageDispatch(actionSetErrorMessage("Failed to add project: " + reason.message));
               })
            break;
         }

         case FRPActionType.UpdateProject:
            {
               FRPProjectPersistence.setInstance(new FRPProjectPersistence(SettingsManager.getSystemSetting("frp.projectsElasticIndex", ""), FRPProjectPersistence.getInstance().userId));
               FRPProjectPersistence.getInstance().saveObject(action.payload.item)
                  .catch((reason) => {
                     appMessageDispatch(actionSetErrorMessage("Failed to update project: " + reason.message))
                  })

               break;
            }

         case FRPActionType.DeleteProject: {
            FRPProjectPersistence.getInstance().deleteObject(action.payload.id)
               .catch((reason) => {
                  appMessageDispatch(actionSetErrorMessage("Failed to delete project: " + reason.message))
               })
            break;
         }

         case FRPActionType.AddElement: {
            if (nextState.selectedProject && nextState.selectedProject.esIndexName) {
               let bs = new FRPElementPersistence(nextState.selectedProject.esIndexName, "kbe@viamap.net");
               FRPElementPersistence.setInstance(bs);

               FRPElementPersistence.getInstance().saveObject(encodeElementStateDataToPersistence(action.payload.item))
                  .catch((reason) => {
                     appMessageDispatch(actionSetErrorMessage("Failed to save SFE: " + reason.message))
                  })

               if (action.payload.item.type === FRPElementType.Element) {
                  const element = action.payload.item as FRPElement;
                  const oldElement = oldState.elements[action.payload.id];
                  const statusChanged = oldElement?.status !== element.status;

                  if (statusChanged && element.status && nextState.childrenLookupTable[element.id]?.length > 0) {
                     const allNestedChildrenIds = FRPFunc.allNestedChildrenIds(element.id, nextState.childrenLookupTable);

                     allNestedChildrenIds.forEach(childId => {
                        const childElement = nextState.elements[childId];
                        if (childElement && childElement.type === FRPElementType.Element) {
                           FRPElementPersistence.getInstance().saveObject(encodeElementStateDataToPersistence(childElement))
                              .catch((reason) => {
                                 appMessageDispatch(actionSetErrorMessage(`Failed to save child element ${childId}: ${reason.message}`))
                              })
                        }
                     });
                  }
               }
            }
            break;
         }

         case FRPActionType.DeleteElement: {
            if (nextState.selectedProject && nextState.selectedProject.esIndexName) {
               let bs = new FRPElementPersistence(nextState.selectedProject.esIndexName, "kbe@viamap.net");
               FRPElementPersistence.setInstance(bs);
               let ids = Array.isArray(action.payload.ids) ? action.payload.ids : [action.payload.ids];
               for (let id of ids) {
                  FRPElementPersistence.getInstance().deleteObject(id)
                     .catch((reason) => {
                        appMessageDispatch(actionSetErrorMessage("Failed to delete SFE: " + reason.message))
                     })
               }
            }
            break;
         }
         default: {
            // No persistence needed
         }
      }
   }

   function broadcastChanges(state: FRPState, action: FRPActions) {

      if (BroadcastedChanges.includes(action.type) && state.selectedProject?.id) {
         MitWebsocket.send({ type: "updateChannel", action: { channel: state.selectedProject.id } });
      } else {
         console.log("Action type not in BroadcastedChanges, skipping broadcast");
      }
   }

   return (
      <FRPContext.Provider
         value={{
            state: frpState,
            dispatch: GenericTransactionManager.dispatchMiddleware<FRPActions>(frpDispatch, transactionalFRPReducer),
         }}
      >
         <FRPNotification >
            {props.children}
         </FRPNotification>
      </FRPContext.Provider>
   )
}





